import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux'; // Import useSelector and useDispatch
import { logout, selectCurrentUser, selectIsAuthenticated } from '../../features/auth/authSlice'; // Import auth selectors and logout action
import Icon from '../AppIcon';
import Button from './Button';

const Header = () => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const userMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);

  const user = useSelector(selectCurrentUser); // Get user from Redux store
  const isAuthenticated = useSelector(selectIsAuthenticated); // Get isAuthenticated from Redux store
  const dispatch = useDispatch(); // Initialize useDispatch

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef?.current && !userMenuRef?.current?.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
      if (mobileMenuRef?.current && !mobileMenuRef?.current?.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleUserMenuToggle = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleLogout = () => {
    dispatch(logout()); // Dispatch logout action
    setIsUserMenuOpen(false);
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  const Logo = () => (
    <Link to="/" className="flex items-center space-x-2"> {/* Changed to root path */}
      <img  src="/assets/images/homelogo.png" alt="Logo" className="w-18 h-10" />
    </Link>
  );

  const NavigationItems = ({ mobile = false }) => (
    <>
      <Link
        to="/"
        className={`${
          mobile ? 'block px-3 py-2 text-base' : 'px-3 py-2 text-sm'
        } font-medium rounded-md transition-colors duration-150 ${
          isActive('/')
            ? 'text-primary bg-primary/10' :'text-foreground hover:text-primary hover:bg-primary/5'
        }`}
      >
        Home
      </Link>

    
      {/* <Link
        to="/hotels"
        className={`${
          mobile ? 'block px-3 py-2 text-base' : 'px-3 py-2 text-sm'
        } font-medium rounded-md transition-colors duration-150 ${
          isActive('/hotels')
            ? 'text-primary bg-primary/10' :'text-foreground hover:text-primary hover:bg-primary/5'
        }`}
      >
        Hotels
      </Link>
      <Link
        to="/hostels"
        className={`${
          mobile ? 'block px-3 py-2 text-base' : 'px-3 py-2 text-sm'
        } font-medium rounded-md transition-colors duration-150 ${
          isActive('/hostels')
            ? 'text-primary bg-primary/10' :'text-foreground hover:text-primary hover:bg-primary/5'
        }`}
      >
        Hostels
      </Link> */}

      <Link
        to="/property-search-browse"
        className={`${
          mobile ? 'block px-3 py-2 text-base' : 'px-3 py-2 text-sm'
        } font-medium rounded-md transition-colors duration-150 ${
          isActive('/property-search-browse')
            ? 'text-primary bg-primary/10' :'text-foreground hover:text-primary hover:bg-primary/5'
        }`}
      >
        Find Properties
      </Link>
      <Link
        to="/about"
        className={`${
          mobile ? 'block px-3 py-2 text-base' : 'px-3 py-2 text-sm'
        } font-medium rounded-md transition-colors duration-150 ${
          isActive('/about')
            ? 'text-primary bg-primary/10' :'text-foreground hover:text-primary hover:bg-primary/5'
        }`}
      >
        About Us
      </Link>
    </>
  );

  const UserMenu = () => (
    <div className="relative" ref={userMenuRef}>
      <button
        onClick={handleUserMenuToggle}
        className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-foreground hover:text-primary hover:bg-primary/5 transition-colors duration-150"
      >
        <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center">
          <Icon name="User" size={16} color="white" />
        </div>
        <span className="hidden md:block">{user?.name || 'Account'}</span>
        <Icon name="ChevronDown" size={16} className={`transition-transform duration-150 ${isUserMenuOpen ? 'rotate-180' : ''}`} />
      </button>

      {isUserMenuOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-card border border-border rounded-md shadow-lg z-50 animate-slide-down">
          <div className="py-1">
            <Link
              to="/user-dashboard"
              className="block px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors duration-150"
              onClick={() => setIsUserMenuOpen(false)}
            >
              <div className="flex items-center space-x-2">
                <Icon name="LayoutDashboard" size={16} />
                <span>My Dashboard</span>
              </div>
            </Link>
            <Link
              to="/user-dashboard?showProfile=true"
              className="block px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors duration-150"
              onClick={() => setIsUserMenuOpen(false)}
            >
              <div className="flex items-center space-x-2">
                <Icon name="User" size={16} />
                <span>My Profile</span>
              </div>
            </Link>
            <Link
              to="/saved-properties"
              className="block px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors duration-150"
              onClick={() => setIsUserMenuOpen(false)}
            >
              <div className="flex items-center space-x-2">
                <Icon name="Heart" size={16} />
                <span>Saved Properties</span>
              </div>
            </Link>
            <hr className="my-1 border-border" />
            <button
              onClick={handleLogout}
              className="block w-full text-left px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors duration-150"
            >
              <div className="flex items-center space-x-2">
                <Icon name="LogOut" size={16} />
                <span>Sign Out</span>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const AuthButtons = () => (
    <div className="flex items-center space-x-2">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/login')}
        className="text-foreground hover:text-primary"
      >
        Sign In
      </Button>
      <Button
        variant="default"
        size="sm"
        onClick={() => navigate('/register')}
      >
        Sign Up
      </Button>
    </div>
  );

  return (
    <header className="fixed top-0 left-0 right-0 z-40 bg-background/95 backdrop-blur-nav border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Logo />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            <NavigationItems />
          </nav>

          {/* Desktop User Menu / Auth Buttons */}
          <div className="hidden md:flex items-center">
            {isAuthenticated ? <UserMenu /> : <AuthButtons />}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={handleMobileMenuToggle}
              className="p-2 rounded-md text-foreground hover:text-primary hover:bg-primary/5 transition-colors duration-150"
            >
              <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={24} />
            </button>
          </div>
        </div>
      </div>
      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-background border-b border-border animate-slide-down" ref={mobileMenuRef}>
          <div className="px-4 pt-2 pb-3 space-y-1">
            <NavigationItems mobile />
            
            {/* Mobile Auth Section */}
            <div className="pt-4 border-t border-border">
              {isAuthenticated ? (
                <div className="space-y-1">
                  <div className="flex items-center space-x-3 px-3 py-2">
                    <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center">
                      <Icon name="User" size={16} color="white" />
                    </div>
                    <span className="text-sm font-medium text-foreground">{user?.name}</span>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-3 py-2 text-base font-medium text-foreground hover:text-primary hover:bg-primary/5 rounded-md transition-colors duration-150"
                  >
                    <div className="flex items-center space-x-2">
                      <Icon name="LogOut" size={16} />
                      <span>Sign Out</span>
                    </div>
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Button
                    variant="ghost"
                    fullWidth
                    onClick={() => navigate('/login')}
                    className="justify-start text-foreground hover:text-primary"
                  >
                    Sign In
                  </Button>
                  <Button
                    variant="default"
                    fullWidth
                    onClick={() => navigate('/register')}
                  >
                    Sign Up
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
